import { iAreaSimpelModel } from "../models/apiModels/areaSimpel";
import { iFacilitySimpelModel } from "../models/apiModels/facilitySimpel";
import { IStartSigningRequestModel } from "../models/apiModels/requestModels/startSigningRequestModel";
import { IStartSigningResponseModel } from "../models/apiModels/responseModels/startSigningResponseModel";
import { iBaseBackgroundBlock } from "../models/blockTypes/baseBackgroundBlock";
import { iBaseBlock, iBaseBlockProperties } from "../models/blockTypes/baseBlock";
import { iFlattenedArea } from "../models/componentModels/flattenedArea";
import { iArea } from "../models/contextModels/area";
import { iCoordinates } from "../models/coordinates";
import { eAreaType } from "../models/enum/areaType";
import { clientLogError, clientLogInfo, clientLogWarn } from "./clientLoggingHelper";
import { GetCountryCode, GetISOCultureCode } from "./cultureHelper";

//SHOULD ONLY HAVE CODE THAT IS SHARED BETWEEN CLIENT AND SERVER, NO SERVER SPECIFIC CALLS!
export {
    getAbsoluteUrlClient as GetAbsoluteUrlClient,
    getDistance as GetDistance,
    removeTrailingSlashFromUrl as RemoveTrailingSlashFromUrl,
    getFormattedDate as GetFormattedDate,
    stripHtml as StripHtml,
    flattenAreaHierarchy as FlattenAreaHierarchy,
    flattenRegionAreaHierarchy as FlattenRegionAreaHierarchy,
    searchFlattenedAreaHierarchy as SearchFlattenedAreaHierarchy,
    searchAreaHierarchy as SearchAreaHierarchy,
    getBackgroundColorClass as GetBackgroundColorClass,
    getBlockWrapperClasses as GetBlockWrapperClasses,
    generateUUID as GenerateUUID,
    hashString as HashString,
    calculateAge as CalculateAge,
    filterAllFacilitiesFromAreaHierarchy as FilterAllFacilitiesFromAreaHierarchy,
    getDialCodeCountry as GetDialCodeCountry,
    getSMApiUrl as GetSMApiUrl,
    getSMApiPayexCallbackUrl as GetSMApiPayexCallbackUrl,
    translateWithReplace as TranslateWithReplace,
    replaceOrgName as ReplaceOrgName,
    getSizeGroupsShortNames as GetSizeGroupsShortNames,
    formatNumberToDecimals as FormatNumberToDecimals,
    doUseSigning as DoUseSigning,
    startSigningProcedure as StartSigningProcedure,
    createDateWithoutTimezone as CreateDateWithoutTimezone,
    getSpecialISOCultureCode as GetSpecialISOCultureCode,
    getClientDomainUrl as GetClientDomainUrl,
    getBaseUrl as GetBaseUrl,
    getLangAndCultureClient as GetLangAndCultureClient,
    hasGsSessionCookieClient as HasGsSessionCookieClient
};

function hasGsSessionCookieClient(): boolean {
    if (typeof document === "undefined") return false; // SSR safety

    // Find the cookie value using a regular expression
    const match = document.cookie.match(/(?:^|;\s*)gsSessionId=([^;]*)/);
    const gsSessionId = match ? decodeURIComponent(match[1]) : null;

    if (!gsSessionId) {
        clientLogWarn("clientUtilityHelper.ts", "hasGsSessionCookieClient", "User has been inactive to long no gsSessionId cookie found");
    }

    return !!gsSessionId;
}

const getLangAndCultureClient = (pathPrefix: string) => {

    pathPrefix = pathPrefix.replace(/^\/+|\/+$/g, '');
    const culture = pathPrefix;
    const language = GetISOCultureCode(pathPrefix);
    const country = GetCountryCode(pathPrefix);
    return { culture, language, country };
}

const getBaseUrl = () => {
    if (typeof window !== "undefined") {
        const { protocol, host } = window.location;
        return `${protocol}//${host}`;
    }
    return ""; // Return an empty string if window is not available (e.g., during SSR)
};

function getClientDomainUrl(country: string): string {
    switch (country.toLowerCase()) {
        case "no":
        case "nb":
        case "nn":
            return (process.env.NEXT_PUBLIC_FRONTEND_URL_NO || process.env.NEXT_PUBLIC_FRONTEND_URL) as string;
        default:
            return (process.env.NEXT_PUBLIC_FRONTEND_URL || "https://www.greenstorage.com") as string;
    }
}

async function startSigningProcedure(
    contractId: string,
    country: string,
    customerId: string,
    locale: string,
    redirectFailure: string,
    redirectSuccess: string
): Promise<string | null> {

    const baseUrl = getClientDomainUrl(country);

    if (redirectFailure && !redirectFailure.startsWith(baseUrl)) {
        redirectFailure = `${baseUrl}${redirectFailure}`;
    }

    if (redirectSuccess && !redirectSuccess.startsWith(baseUrl)) {
        redirectSuccess = `${baseUrl}${redirectSuccess}`;
    }

    const startSigningRequestModel: IStartSigningRequestModel = {
        contractIds: [contractId],
        customerId: customerId,
        locale: locale,
        redirectFailure: redirectFailure,
        redirectSuccess: redirectSuccess
    };

    const signResponse = await fetch("/api/checkout/startSigning", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Country: country,
        },
        body: JSON.stringify(startSigningRequestModel),
    });

    if (!signResponse.ok) {
        clientLogError("clientUtilityHelper.tsx", "startSigningProcedure", "startSigning response not ok", startSigningRequestModel);
        return null;
    }

    const signResponseResult = await signResponse.json() as IStartSigningResponseModel;
    if (signResponseResult === null || signResponseResult === undefined) {
        clientLogError("clientUtilityHelper.tsx", "startSigningProcedure", "startSigning response ok but null", startSigningRequestModel);
        return null;
    }

    return signResponseResult.redirectUrl;
}

function doUseSigning(country: string): boolean {
    const countriesUsingSigningString = process.env.NEXT_PUBLIC_COUNTRIES_USING_SIGNING as string;
    const countriesUsingSigning = countriesUsingSigningString.split(",");

    return countriesUsingSigning.includes(country.toLowerCase());
}

function replaceOrgName(translation: string, temporaryOrgName?: string, useTemporaryOrgName: boolean = false): string {
    if (translation.indexOf("{organization}") === -1) {
        return translation;
    }

    if (!temporaryOrgName || !useTemporaryOrgName) {
        return translateWithReplace(translation, { organization: process.env.NEXT_PUBLIC_ORG_NAME as string });
    }

    return translateWithReplace(translation, { organization: temporaryOrgName });
}

function translateWithReplace(translation: string, replacements: { [key: string]: string }): string {
    let result = translation;

    for (const key in replacements) {
        if (!replacements[key] || key === "undefined" || translation.indexOf(`{${key}}`) === -1) {
            continue;
        }

        result = result.replace(`{${key}}`, replacements[key]);
    }

    return result;
}

function getAbsoluteUrlClient(url: string, baseUmbracoUrl?: string): string {
    clientLogInfo('clientUtilityHelper.ts', 'getAbsoluteUrlClient', 'url', url, 'baseUmbracoUrl', baseUmbracoUrl);
    try {
        if (!url || url.startsWith("http")) {
            return url;
        }

        if (baseUmbracoUrl) {
            return `${baseUmbracoUrl}${url}`;
        }

        return `${process.env.NEXT_PUBLIC_AZURE_MEDIA_CDN}${url}`;
    }
    catch (error) {
        clientLogError('clientUtilityHelper.ts', 'getAbsoluteUrlClient', 'url', url, 'baseUmbracoUrl', baseUmbracoUrl, 'Error', error);
        return "";
    }
}

function createDateWithoutTimezone(dateString: string): Date {
    const [year, month, day] = dateString.split("T")[0].split("-").map(Number);
    // Create a Date object in UTC without applying time zone conversion
    return new Date(Date.UTC(year, month - 1, day));
}

async function hashString(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

function getDistance(coordinates1: iCoordinates, coordinates2: iCoordinates): number {
    const toRad = (x: number) => x * Math.PI / 180;
    const R = 6371; // Radius of the Earth in km
    const dLat = toRad(coordinates2.latitude - coordinates1.latitude);
    const dLon = toRad(coordinates2.longitude - coordinates1.longitude);
    const lat1 = toRad(coordinates1.latitude);
    const lat2 = toRad(coordinates2.latitude);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function removeTrailingSlashFromUrl(url: string): string {
    return url.replace(/\/$/, "");
}

function getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function stripHtml(html: string): string {
    if (!html) {
        return "";
    }
    return html.replace(/<\/?[^>]+(>|$)/g, "");
}

function flattenAreaHierarchy(areas: iAreaSimpelModel[]): iFlattenedArea[] {
    const result: iFlattenedArea[] = [];

    const traverse = (area: iAreaSimpelModel, currentCityArea: iAreaSimpelModel | null) => {
        let cityArea = currentCityArea;

        if (area.areaType === eAreaType.City) {
            cityArea = area;
            result.push({ area: cityArea, facilities: [] });
        }

        if (cityArea) {
            const cityEntry = result.find(entry => entry.area.id === cityArea.id);
            if (cityEntry) {
                cityEntry.facilities.push(...area.facilities);
            }
        }

        area.subAreas.forEach(subArea => {
            traverse(subArea, cityArea);
        });
    };


    areas.forEach(area => traverse(area, null));
    return result;
}

function getSizeGroupsShortNames(): string[] {
    return ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "C", "KS"];
}

function formatNumberToDecimals(value: number | null): string {
    if (value === null) {
        return "";
    }

    if (value % 1 === 0.25) {
        return value.toFixed(2); // Show two decimals if it's .25
    }

    const rounded = Math.round(value * 10) / 10; // Round to one decimal place
    if (rounded % 1 === 0) {
        return `${rounded}`; // No decimals if it's an integer
    }

    return rounded.toFixed(1); // Show one decimal otherwise
}

function flattenRegionAreaHierarchy(areas: iAreaSimpelModel[]): iFlattenedArea[] {
    const result: iFlattenedArea[] = [];

    const traverse = (area: iAreaSimpelModel, currentRegionArea: iAreaSimpelModel | null) => {
        let regionArea = currentRegionArea;

        if (area.areaType === eAreaType.Region) {
            regionArea = area;
            result.push({ area: regionArea, facilities: [] });
        }

        if (regionArea) {
            const regionEntry = result.find(entry => entry.area.id === regionArea.id);
            if (regionEntry) {
                regionEntry.facilities.push(...area.facilities);
            }
        }

        area.subAreas.forEach(subArea => {
            traverse(subArea, regionArea);
        });
    };

    areas.forEach(area => traverse(area, null));
    return result;
}

function filterAllFacilitiesFromAreaHierarchy(areas: iAreaSimpelModel[]): iFacilitySimpelModel[] {
    const result: iFacilitySimpelModel[] = [];

    const traverse = (area: iAreaSimpelModel) => {
        result.push(...area.facilities);
        area.subAreas.forEach(subArea => {
            traverse(subArea);
        });
    };

    areas.forEach(area => traverse(area));
    return result;
}

function searchAreaHierarchy(query: string, areas: iAreaSimpelModel[]): iFlattenedArea[] {
    const lowerCaseQuery = query.toLowerCase();

    const traverseSearch = (area: iAreaSimpelModel, query: string): iFlattenedArea[] => {
        const subResult: iFlattenedArea[] = [];
        const areaMatches = area.name.toLowerCase().includes(lowerCaseQuery);

        if (areaMatches) {
            subResult.push({ area, facilities: area.facilities });
        }

        const matchingFacilities = area.facilities.filter(facility => {
            const facilityMatches = facility.name.toLowerCase().includes(lowerCaseQuery) ||
                facility.streetAddress.toLowerCase().includes(lowerCaseQuery) ||
                facility.city.toLowerCase().includes(lowerCaseQuery);

            return facilityMatches;
        });

        if (matchingFacilities.length > 0 && !subResult.some(r => r.area.id === area.id)) {
            subResult.push({ area, facilities: matchingFacilities });
        }

        const subAreaResult: iFlattenedArea[] = area.subAreas.map(subArea => traverseSearch(subArea, query)).flat();
        subResult.push(...subAreaResult);
        return subResult;
    };

    const result = areas.map(subArea => traverseSearch(subArea, lowerCaseQuery)).flat();

    return result;

}

function searchFlattenedAreaHierarchy(query: string, flattenedAreas: iFlattenedArea[]): iFlattenedArea[] {
    const lowerCaseQuery = query.toLowerCase();
    let filteredAreas = flattenedAreas.map(({ area, facilities }) => {
        const areaMatches = area.name.toLowerCase().includes(lowerCaseQuery);

        if (areaMatches) {
            return { area, facilities };
        }

        const matchingFacilities = facilities.filter(facility => {
            const facilityMatches = facility.name.toLowerCase().includes(lowerCaseQuery) ||
                facility.streetAddress.toLowerCase().includes(lowerCaseQuery) ||
                facility.city.toLowerCase().includes(lowerCaseQuery);

            return facilityMatches;
        });

        if (matchingFacilities.length > 0) {
            return { area, facilities: matchingFacilities };
        }

        return null;

    }).filter(result => result !== null);

    return filteredAreas;
}

function getBackgroundColorClass(model?: iBaseBackgroundBlock) {
    if (!model) {
        return 'bg-see-through';
    }

    const backgroundColor = model.properties.backgroundColor as { label: string, color: string };
    const backgroundColorAlias = backgroundColor ? backgroundColor.label.toString().toLowerCase().replace(/ /g, "-") : 'see-through';

    switch (backgroundColorAlias) {
        case 'light-greenstorage-green':
            return 'bg-light-gs-green';
        case 'medium-greenstorage-green':
            return 'bg-medium-gs-green';
        case 'greenstorage-green':
            return 'bg-gs-green';
        case 'white':
            return 'bg-white';
        case 'see-through':
        default:
            return 'bg-see-through';
    }
}

function getBlockWrapperClasses(block: iBaseBlock, onlyRoundedInDesktop: boolean = false, hidePadding: boolean = false): string {
    let cssClasses = "block relative";

    if (isBaseBackgroundBlock(block)) {
        cssClasses += ` ${getBackgroundColorClass(block)}`;

        if (block.properties.showRoundedCorners && cssClasses.indexOf("bg-see-through") === -1) {
            cssClasses += ` ${onlyRoundedInDesktop ? "xl:rounded-3xl" : "rounded-3xl"} ${hidePadding ? "" : "p-6"}`;
        } else if (cssClasses.indexOf("bg-see-through") > -1) {
            cssClasses += ` ${hidePadding ? "" : "px-6 xl:px-0"}`;
        }
    }
    else if (isBaseBlock(block)) {
        if (block.properties.showRoundedCorners) {
            cssClasses += ` ${onlyRoundedInDesktop ? "xl:rounded-3xl" : "rounded-3xl"} ${hidePadding ? "" : "p-6"}`;
        }
    }

    if (hasShowBackgroundProperty(block)) {
        const modelWithShowBackground = block as unknown as iBaseBlockWithShowBackground;
        if (!modelWithShowBackground.properties.showBackground) {
            cssClasses += " hide-background";
        }
    }

    return cssClasses.trim();
}

function isBaseBackgroundBlock(model: iBaseBlock | iBaseBackgroundBlock): model is iBaseBackgroundBlock {
    return (model as iBaseBackgroundBlock).properties?.backgroundColor !== undefined;
}


function isBaseBlock(model: iBaseBlock | iBaseBackgroundBlock): model is iBaseBlock {
    return (model as iBaseBackgroundBlock).properties?.backgroundColor === undefined
        && (model as iBaseBlock).properties?.showRoundedCorners !== undefined;
}

function hasShowBackgroundProperty(model: any): model is { showBackground: boolean } {
    return model.properties?.showBackground !== undefined;
}

interface iBaseBlockWithShowBackground extends iBaseBlock {
    properties: iBaseBlockProperties & {
        showBackground: boolean;
    };
}

function getDialCodeCountry(country: string | undefined) {
    var dialCodeCountry = "";

    if (!country) {
        return "dk";
    }

    switch (country.toLowerCase()) {
        case "no":
        case "nb":
        case "nn":
            dialCodeCountry = "no";
            break;
        case "da":
        case "dk":
            dialCodeCountry = "dk";
            break;
        case "se":
        case "sv":
            dialCodeCountry = "se";
            break;
        default:
            dialCodeCountry = country;
    }

    return dialCodeCountry;
}

function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function getSMApiUrl(country: string): string | null {
    switch (country.toLowerCase()) {
        case "no":
        case "nb":
        case "nn":
            return process.env.API_V2_URL_NO || "https://smapi.greenstorage.no/v2";
        case "da":
        case "dk":
            return process.env.API_V2_URL_DK || "https://smapi.greenstorage.dk/v2";
        case "se":
        case "sv":
            return process.env.API_V2_URL_SV || "https://smapi.greenstorage.se/v2";
        default:
            return null;
    }
}

function getSMApiPayexCallbackUrl(country: string): string | null {
    switch (country.toLowerCase()) {
        case "no":
        case "nb":
        case "nn":
            return process.env.SETTING_PAYEX_URL_CALLBACK_NO || "https://smapi.greenstorage.no/v2/callback/swedbankpay";
        case "da":
        case "dk":
            return process.env.SETTING_PAYEX_URL_CALLBACK_DK || "https://smapi.greenstorage.dk/v2/callback/swedbankpay";
        case "se":
        case "sv":
            return process.env.SETTING_PAYEX_URL_CALLBACK_SV || "https://smapi.greenstorage.se/v2/callback/swedbankpay";
        default:
            return null;
    }
}

function calculateAge(dateOfBirth: string): number {
    const [year, month, day] = dateOfBirth.split('-').map(Number);
    const birthDate = new Date(year, month - 1, day); // Months are zero-based in JavaScript Date
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    // Adjust age if the current date is before the birth date in the current year
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
}

function getSpecialISOCultureCode(cultureString: string) {
    let cultureCode = getISOCultureCode(cultureString);

    if (cultureCode === "nb") {
        cultureCode = "no";
    }

    return cultureCode;
}

function getISOCultureCode(cultureString: string) {
    var cultureCode = "";

    if (cultureString.includes("-") || cultureString.includes("_")) {
        let cultureParts = cultureString.split(/[-_]/);
        cultureCode = cultureParts.length > 1 ? cultureParts[1] : cultureParts[0];
    } else if (cultureString.length === 2) {
        cultureCode = cultureString;
    }

    switch (cultureCode.toLowerCase()) {
        case "no":
        case "nb":
        case "nn":
            cultureCode = "nb";
            break;
        case "da":
        case "dk":
            cultureCode = "da";
            break;
        case "se":
            cultureCode = "sv";
        case "en":
            cultureCode = "en";
            break;
    }

    return cultureCode;
}